import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { mentorMySubmissions } from "../../lib/api";

const STATUS_LABEL = {
  DRAFT: { label: "Entwurf", c: "text-white/50 border-white/20" },
  SUBMITTED: { label: "Eingereicht", c: "text-[#D4AF37] border-[#D4AF37]/40" },
  IN_REVIEW: { label: "In Prüfung", c: "text-amber-200 border-amber-200/40" },
  REVISION_REQUESTED: { label: "Überarbeitung angefragt", c: "text-amber-300 border-amber-300/40" },
  RESUBMITTED: { label: "Erneut eingereicht", c: "text-[#D4AF37] border-[#D4AF37]/40" },
  APPROVED: { label: "Bestanden", c: "text-emerald-300 border-emerald-400/40" },
  REJECTED: { label: "Nicht bestanden", c: "text-rose-300 border-rose-400/40" },
  WITHDRAWN: { label: "Zurückgezogen", c: "text-white/40 border-white/10" },
};

export default function MentorMySubmissions() {
  const [subs, setSubs] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mentorMySubmissions()
      .then(d => setSubs(d.submissions || []))
      .catch(e => setErr(e?.response?.data?.detail || String(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-[#F5EFE7] pt-24" data-testid="mentor-my-submissions">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/academy/dashboard" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white/80 mb-6">
          <ArrowLeft size={14} /> Zurück zum Dashboard
        </Link>

        <div className="mb-8">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mb-1">SAT MENTOR</div>
          <h1 className="text-3xl font-serif">Meine Einreichungen</h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">
            Deine Einreichungen bleiben privat. Nur berechtigte Reviewer sehen sie.
            Zwölf Pflicht-Einreichungen sind für die Zertifizierungsreife nötig.
          </p>
        </div>

        {loading && <div className="text-white/50 text-sm">Lade …</div>}
        {err && <div className="text-rose-300 text-sm">{err}</div>}
        {!loading && !err && subs.length === 0 && (
          <div className="text-white/60 text-sm border border-white/10 rounded p-6"
               data-testid="mentor-submissions-empty">
            Noch keine Einreichungen. Sobald ein Modul geöffnet ist, kannst du hier
            deine Arbeit einreichen. Fallmaterial ausschliesslich anonymisiert.
          </div>
        )}

        <div className="space-y-3">
          {subs.map(s => {
            const meta = STATUS_LABEL[s.status] || STATUS_LABEL.DRAFT;
            return (
              <div key={s.id}
                   className="border border-white/10 rounded p-4"
                   data-testid={`my-submission-${s.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-lg font-serif truncate">{s.assignment_key}</div>
                    <div className="text-xs text-white/40 mt-1">
                      Version {s.current_version}
                      {s.submitted_at ? ` · eingereicht ${new Date(s.submitted_at).toLocaleString("de-CH")}` : ""}
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-widest uppercase border ${meta.c}`}
                        data-testid={`status-badge-${s.id}`}>
                    {meta.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 border-t border-white/10 pt-6 text-xs text-white/40 flex items-start gap-2">
          <ShieldCheck size={14} className="text-[#D4AF37]" />
          <span>
            Deine Texte, Reflexionen und Anhänge werden vertraulich behandelt und
            niemals mit anderen Lernenden geteilt. Reviewer sind serverseitig
            berechtigt und protokolliert.
          </span>
        </div>
      </div>
    </div>
  );
}
